import { Amplify } from 'aws-amplify';
import { confirmSignIn, fetchAuthSession, getCurrentUser, signIn, signOut as amplifySignOut } from 'aws-amplify/auth';
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { getRuntimeConfig, type RuntimeConfig } from '../config/runtime';
import { db, ensureSettings, purgeUserData } from '../data/db';
import { seedDemoData } from '../data/demo';

export interface AuthUser { id: string; email: string; local: boolean; offline: boolean; }

interface AuthContextValue {
  user: AuthUser | undefined;
  loading: boolean;
  error: string | undefined;
  needsNewPassword: boolean;
  config: RuntimeConfig | undefined;
  login(email: string, password: string): Promise<void>;
  confirmNewPassword(password: string): Promise<void>;
  logout(forceDiscard?: boolean): Promise<void>;
  accessToken(): Promise<string | undefined>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);
const TRUSTED_USER_KEY = 'kemi.trusted-user';
const LOCAL_USER: AuthUser = { id: 'local-development-user', email: 'local@kemicontrol.dev', local: true, offline: false };

function trustedUser(): AuthUser | undefined {
  const stored = localStorage.getItem(TRUSTED_USER_KEY);
  if (!stored) return undefined;
  try {
    const candidate = JSON.parse(stored) as Partial<AuthUser>;
    if (typeof candidate.id !== 'string' || !candidate.id || typeof candidate.email !== 'string' || !candidate.email || candidate.local === true) return undefined;
    return { id: candidate.id, email: candidate.email, local: false, offline: true };
  } catch {
    return undefined;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser>();
  const [config, setConfig] = useState<RuntimeConfig>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>();
  const [needsNewPassword, setNeedsNewPassword] = useState(false);

  useEffect(() => {
    let active = true;
    let initializing = false;
    let usingOfflineSession = false;
    let runtimeReady = false;
    const trusted = trustedUser();

    const enterTrustedOffline = async () => {
      if (!trusted) return false;
      await ensureSettings(trusted.id);
      usingOfflineSession = true;
      if (active) { setError(undefined); setUser(trusted); setLoading(false); }
      return true;
    };

    const initializeOnline = async () => {
      if (initializing) return;
      initializing = true;
      try {
        let runtime: RuntimeConfig;
        try {
          runtime = await getRuntimeConfig();
        } catch (runtimeError) {
          if (await enterTrustedOffline()) return;
          if (active) setError(runtimeError instanceof Error ? runtimeError.message : 'Falha de configuração.');
          return;
        }
        if (!active) return;
        runtimeReady = true;
        setConfig(runtime);
        if (runtime.localMode) {
          await seedDemoData(LOCAL_USER.id);
          usingOfflineSession = false;
          if (active) { setUser(LOCAL_USER); setLoading(false); }
          return;
        }
        Amplify.configure({ Auth: { Cognito: { userPoolId: runtime.userPoolId, userPoolClientId: runtime.userPoolClientId, signUpVerificationMethod: 'code' } } });
        const current = await getCurrentUser();
        const resolved = { id: current.userId, email: current.signInDetails?.loginId ?? current.username, local: false, offline: false };
        localStorage.setItem(TRUSTED_USER_KEY, JSON.stringify(resolved));
        await ensureSettings(resolved.id);
        usingOfflineSession = false;
        if (active) { setError(undefined); setUser(resolved); }
      } catch (authError) {
        if (!navigator.onLine && await enterTrustedOffline()) {
          return;
        } else if (authError instanceof Error && !/not authenticated|No current user/i.test(authError.message)) {
          if (active) setError(authError.message);
        } else if (active) {
          setUser(undefined);
        }
      } finally {
        if (active) setLoading(false);
        initializing = false;
      }
    };

    const retryOnline = () => { if (navigator.onLine && (usingOfflineSession || !runtimeReady)) void initializeOnline(); };
    if (!navigator.onLine && trusted) void enterTrustedOffline();
    else void initializeOnline();
    window.addEventListener('online', retryOnline);
    window.addEventListener('focus', retryOnline);
    return () => { active = false; window.removeEventListener('online', retryOnline); window.removeEventListener('focus', retryOnline); };
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    setError(undefined);
    const result = await signIn({ username: email, password });
    if (result.nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
      setNeedsNewPassword(true);
      return;
    }
    if (!result.isSignedIn) throw new Error('Não foi possível concluir a entrada.');
    const current = await getCurrentUser();
    const resolved = { id: current.userId, email, local: false, offline: false };
    localStorage.setItem(TRUSTED_USER_KEY, JSON.stringify(resolved));
    await ensureSettings(resolved.id);
    setUser(resolved);
  }, []);

  const confirmNewPassword = useCallback(async (password: string) => {
    const result = await confirmSignIn({ challengeResponse: password });
    if (!result.isSignedIn) throw new Error('Não foi possível definir a nova senha.');
    const current = await getCurrentUser();
    const resolved = { id: current.userId, email: current.signInDetails?.loginId ?? current.username, local: false, offline: false };
    localStorage.setItem(TRUSTED_USER_KEY, JSON.stringify(resolved));
    await ensureSettings(resolved.id);
    setNeedsNewPassword(false);
    setUser(resolved);
  }, []);

  const logout = useCallback(async (forceDiscard = false) => {
    if (!user) return;
    const pending = await db.outbox.where('userId').equals(user.id).count();
    if (pending && !forceDiscard) throw new Error('Existem alterações aguardando sincronização. Sincronize ou confirme o descarte permanente.');
    if (!user.local) await amplifySignOut();
    await purgeUserData(user.id);
    localStorage.removeItem(TRUSTED_USER_KEY);
    setUser(undefined);
  }, [user]);

  const accessToken = useCallback(async () => {
    if (!user || user.local || user.offline) return undefined;
    return (await fetchAuthSession()).tokens?.accessToken.toString();
  }, [user]);

  const value = useMemo<AuthContextValue>(() => ({ user, loading, error, needsNewPassword, config, login, confirmNewPassword, logout, accessToken }), [user, loading, error, needsNewPassword, config, login, confirmNewPassword, logout, accessToken]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const value = useContext(AuthContext);
  if (!value) throw new Error('AuthProvider ausente.');
  return value;
}
